/* eslint-disable  no-param-reassign, sonarjs/cognitive-complexity */
import { faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Link from 'next/link';
import PropTypes from 'prop-types';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { HEADER_NAV_NATIONAL_POPOVER } from '../../../brands/default/content/components/header';
import { trackEvent, updateDod } from '../../../helpers/analytics';
import { hashCode } from '../../../helpers/hash';
import ThemeContext from '../../../providers/ThemeProvider';
import { getDealPreviews } from '../../../redux/actions/navigation';
import HeaderNavigationDealCard from './DealCard';

const HeaderNavigationNationalPopover = ({ category, setOpen }) => {
  const theme = useContext(ThemeContext);

  const dispatch = useDispatch();
  const previews = useSelector((state) => state.navigation.previews);
  const deals = useMemo(() => previews[category.url], [previews, category.url]);
  const totalDealsCount = category.subNavigations.reduce((out, item) => {
    if (item.count > 0) {
      out += item.count;
    }

    return out;
  }, 0);

  const [hoveredSubNav, setHoveredSubNav] = useState(null);

  useEffect(() => {
    if (deals === undefined) {
      dispatch(getDealPreviews(category.url, 4));
    }
  }, [dispatch, category.url, deals]);

  if (category.subNavigations.length === 0) {
    return null;
  }

  const handleMouseEnter = (item) => {
    setHoveredSubNav(item);
  };

  const onClickHandler = (name) => {
    updateDod({ findPageName: name });
    trackEvent('Find page nav click');
    setOpen(false);
  };

  return (
    <>
      <div className="navigation-popover">
        <div
          className={`navigation-popover__column ${
            hoveredSubNav?.subSubNavigations?.length > 0 ? 'border-removed' : ''
          }`}
        >
          <ul className="navigation-popover__list">
            <li className="navigation-popover__link--small-only">
              <Link href={category.url ?? ''}>
                <a className="navigation-popover__link ">
                  {HEADER_NAV_NATIONAL_POPOVER.allCategoryDeals(
                    category.linkText,
                  )}{' '}
                  {totalDealsCount > 0 && `(${totalDealsCount})`}
                </a>
              </Link>
            </li>
            {category.subNavigations.map((item, index) => (
              <li
                className={`navigation-popover__item sub-navigation-popover__link ${
                  item.id === hoveredSubNav?.id
                    ? 'navigation-popover__item_hovered'
                    : ''
                }`}
                key={`HNP_li_${index}_${hashCode()}`}
                onMouseEnter={() => handleMouseEnter(item)}
              >
                <Link href={item.url ?? ''}>
                  <a
                    className="navigation-popover__link"
                    onClick={() => setOpen(false)}
                    onKeyDown={() => setOpen(false)}
                    role="link"
                    tabIndex="0"
                  >
                    {item.linkText} {item.count && `(${item.count})`}
                  </a>
                </Link>
                {item?.subSubNavigations &&
                  item?.subSubNavigations.length > 0 && (
                    <FontAwesomeIcon
                      className="arrow-right-subsub-category"
                      icon={faChevronRight}
                    />
                  )}
              </li>
            ))}
          </ul>
        </div>
        {hoveredSubNav?.id && hoveredSubNav?.subSubNavigations?.length > 0 && (
          <div className="navigation-popover__column color_setting">
            <ul className="navigation-popover__list">
              {hoveredSubNav.subSubNavigations.map((subItem, subIndex) => (
                <li
                  className="sub-navigation-popover__link"
                  key={`HNP_sub_li_${subIndex}_${hashCode()}`}
                >
                  <Link href={subItem.url ?? ''}>
                    <a
                      className="sub-navigation-popover__link_a_tag"
                      onClick={() => onClickHandler(subItem.linkText)}
                      onKeyDown={() => onClickHandler(subItem.linkText)}
                      role="link"
                      tabIndex="0"
                    >
                      {subItem.linkText} {subItem.count && `(${subItem.count})`}
                    </a>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        )}
        <div className="navigation-popover__deals ">
          {deals?.map((deal) => (
            <div
              className="navigation-popover__inner"
              key={`HNP_dealcard_${deal.id}_${hashCode()}`}
            >
              <HeaderNavigationDealCard deal={deal} />
            </div>
          ))}
          {!deals &&
            [null, null, null, null].map((_, index) => {
              return (
                <div
                  className="deal-card--placeholder"
                  key={`HNP_deal_card_${index}_${hashCode()}`}
                />
              );
            })}
        </div>
      </div>
      <style jsx>{`
        /* General container to hold all columns */
        .navigation-popover {
          display: flex;
          flex-direction: row;
          width: 100%;
          padding: 15px 15px 15px 0px;
        }

        /* Category and Subcategory Section */
        .navigation-popover__column {
          flex: 1;
          min-width: 330px; /* Set a minimum width, adjust as needed */
          max-width: 300px; /* Set a maximum width, adjust as needed */
          transition: width 0.3s;
        }
        .border-removed {
          border-right: none !important;
        }
        .navigation-popover__list {
          list-style: none;
          padding: 0;
        }
        .navigation-popover__link {
          color: #000000;
          white-space: normal;
          padding: 5px 10px;
          transition: color 0.3s ease;
        }
        .navigation-popover__link:hover {
          color: ${theme.colors.navlinkactive};
        }
        .navigation-popover__link--small-only {
          font-weight: bolder;
          padding-bottom: 4%;
        }
        .navigation-popover__item:hover {
          opacity: 1;
          visibility: visible;
          transform: translateX(0);
        }
        .navigation-popover__item:hover {
          background-color: ${theme.colors.navbackground};
          border-left: 8px solid ${theme.colors.headerprimary};
        }
        .navigation-popover__item_hovered {
          background-color: ${theme.colors.navbackground};
          border-left: 8px solid ${theme.colors.headerprimary};
        }
        .arrow-right-subsub-category {
          margin-left: 3%;
        }
        .color_setting {
          background-color: ${theme.colors.navbackground};
        }
        .sub-navigation-popover__link {
          padding: 5px 10px;
          color: #000000;
          transition: background-color 0.3s ease, color 0.3s ease;
        }
        .sub-navigation-popover__link_a_tag {
          color: #000000;
        }
        .sub-navigation-popover__link_a_tag:hover {
          background-color: ${theme.colors.navbackground};
          color: ${theme.colors.primary};
        }
        /* Deals section - Third column */
        .navigation-popover__deals {
          flex: 2; /* Double the size of the other columns */
          display: flex;
          flex-wrap: wrap;
          justify-content: space-between; /* Distribute images evenly */
          align-items: flex-start; /* Align images at the top */
          padding: 10px;
          align-content: baseline;
        }

        .deal-card--placeholder > * {
          max-width: 100%;
          max-height: 100%;
          width: 100%; /* Ensure the child takes full width */
          height: 100%; /* Ensure the child takes full height */
          box-sizing: border-box; /* Ensure padding and borders are included within the width/height */
          object-fit: cover; /* For images, ensures they cover the parent without distortion */
        }
        .navigation-popover__inner > * {
          max-width: 100%;
          max-height: 100%;
          width: 100%; /* Ensure the child takes full width */
          height: 100%; /* Ensure the child takes full height */
          box-sizing: border-box; /* Ensure padding and borders are included within the width/height */
          object-fit: cover; /* For images, ensures they cover the parent without distortion */
        }
        /* Deal card placeholders (for images) */
        .deal-card--placeholder {
          display: flex;
          justify-content: center;
          align-items: center;
          flex-direction: column; /* Adjust based on content */
          overflow: hidden; /* Prevent overflow */
          width: 100%; /* Ensure it takes full width */
          height: 100%; /* Ensure it takes full height */
        }
        .navigation-popover__inner {
          display: flex;
          justify-content: center;
          align-items: center;
          flex-direction: column; /* Adjust based on content */
          overflow: hidden; /* Prevent overflow */
          width: 46%;
          margin: 1%;
        }
        /* Ensuring no space on the right-hand side */
        .navigation-popover__deals > .deal-card--placeholder:last-child {
          margin-right: 0;
        }
      `}</style>
    </>
  );
};

HeaderNavigationNationalPopover.propTypes = {
  category: PropTypes.object,
  setOpen: PropTypes.func,
};

export default HeaderNavigationNationalPopover;
